<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { api } from "@/api/client";
import { ConflictError, type SharedoVersion } from "@/api/types";
import FormField from "@/components/FormField.vue";
import Spinner from "@/components/Spinner.vue";
import { pushToast } from "@/composables/useToasts";
import { ensurePermission } from "@/composables/useNotifications";

const router = useRouter();

const customer = ref("");
const customerTouched = ref(false);

const dnsZones = ref<{ zone: string; default?: boolean }[]>([]);
const dnsZone = ref("test.sharedo.cloud");

// Operate version is a two-step pick:
//   1. channel: "release" (ring=staging, last 5 semver) | "playground" (ring=playground, all)
//   2. specific version from that channel's list
type Channel = "release" | "playground";
const channel = ref<Channel>("release");
const versions = ref<SharedoVersion[]>([]);
const versionsLoading = ref(false);
const versionsWarning = ref<string | null>(null);
const sharedoVersion = ref<string>("");

// EE version selector: "pinned" | "latest" | "custom". "custom" reveals a free-text
// field (eeVersionCustom) so an operator can pin an explicit pre-published build (e.g.
// v0.0.0.1, the platform-upgrade EE with the Kurrent client). The backend accepts any
// version string verbatim (download-ee-package-blob.ps1 takes an explicit version).
const eeVersion = ref("pinned");
const eeVersionCustom = ref("");
// The value actually submitted: the custom text when "custom" is picked, else the select.
const effectiveEeVersion = computed(() =>
  eeVersion.value === "custom" ? eeVersionCustom.value.trim() : eeVersion.value,
);
// Opt-in admin-user seeding (task AL). Default off — newer playground
// builds derived from blank-sharedo ship a StartupWizard that owns
// first-admin bootstrap; ticking this for them would fight the wizard.
// Older builds need it ticked to land a usable login. See the form-helper
// copy under the checkbox for the operator-facing explanation.
const seedAdminUser = ref(false);
// Sidecar backend (admin-only opt-in): "aci" (default) or "aks" (shared AKS cluster).
const sidecarBackend = ref<"aci" | "aks">("aci");
// .NET 10 v2 tier (admin-only opt-in): secured Elasticsearch 9 + net10 indexer-v2/search-v2.
// Default off → legacy ES 6 + 4.8 indexer. Requires the selected release to carry the net10 package.
const searchV2 = ref(false);
// Umbrella platform-upgrade opt-in (admin-only): KurrentDB 26.0 (gRPC/TLS) instead of
// EventStore 4.1.2, OpenIddict identity, and the ES 9 / net10 tier. Implies searchV2 — when
// on, the searchV2 toggle is forced on + disabled (see template). Requires a platform-upgrade build.
const platformUpgrade = ref(false);
const submitting = ref(false);
const submitError = ref<string | null>(null);

// ── "Preconfigure the environment" (task BP) ──────────────────────────────
//
// Newer builds ship a StartupWizard that the operator would otherwise have to
// click through after the deploy. Tick this and we collect the same answers
// here (as a flat form, not a wizard) and the orchestrator replays them at the
// end of the deploy. Mutually exclusive with seedAdminUser — the wizard's own
// admin-user step creates the administrator.
//
// Option values must match the trial's seeded data. They're duplicated from
// src/lib/preconfigure.ts (the web app builds separately) — keep in sync.
const preconfigureEnabled = ref(false);

const COUNTRIES = [
  { value: "gbr", label: "United Kingdom" },
  { value: "can", label: "Canada" },
  { value: "aus", label: "Australia" },
  { value: "usa", label: "United States of America" },
];
// Only these three are seeded `enabled = 1`; CAD/AUD exist but are disabled,
// so the wizard wouldn't consider them selectable.
const CURRENCIES = [
  { value: "GBP", label: "Pound Sterling (£)" },
  { value: "USD", label: "US Dollar ($)" },
  { value: "EUR", label: "Euro (€)" },
];
const LANGUAGES = [
  { value: "eng", label: "English" },
  { value: "deu", label: "German" },
  { value: "spa", label: "Spanish" },
];
const PACKAGES = [
  {
    value: "core-config-pack",
    label: "Core Configuration Pack",
    help: "Participant types/roles, work types, allocation + business rules, themes, option sets. Recommended for every new environment.",
  },
  {
    value: "developer-pack",
    label: "Developer Configuration Pack",
    help: "A functional slice of the legal accelerator (contract → SoW → instruction → matter) for exercising an end-to-end flow. Requires the Core pack.",
  },
];

const orgName = ref("");
const orgShortName = ref("");
const orgShortNameEdited = ref(false);
const countrySystemName = ref("gbr");
const currencyIsoCode = ref("GBP");
const languageIsoCode = ref("eng");
const adminFirstname = ref("");
const adminSurname = ref("");
const adminUsername = ref("");
const adminPassword = ref("");
const selectedPackages = ref<string[]>(["core-config-pack"]);
const preconfigureTouched = ref(false);

// Mirror the wizard's own derivation: short name tracks the org name until the
// operator edits it by hand.
watch(orgName, (v) => {
  if (!orgShortNameEdited.value) {
    orgShortName.value = v.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12);
  }
});

// developer-pack can't install without core-config-pack, so ticking it pulls
// core in (and unticking core drops developer). Validated server-side too.
watch(selectedPackages, (v) => {
  if (v.includes("developer-pack") && !v.includes("core-config-pack")) {
    selectedPackages.value = ["core-config-pack", ...v];
  }
});

// Same policy the wizard enforces client-side.
const passwordErrors = computed(() => {
  const p = adminPassword.value;
  if (!p) return ["Required."];
  const e: string[] = [];
  if (p.length < 8) e.push("8+ characters");
  if (!/[A-Z]/.test(p)) e.push("an uppercase letter");
  if (!/[a-z]/.test(p)) e.push("a lowercase letter");
  if (!/[0-9]/.test(p)) e.push("a number");
  if (!/[^A-Za-z0-9]/.test(p)) e.push("a special character");
  return e.length > 0 ? [`Needs ${e.join(", ")}.`] : [];
});

const shortNameError = computed(() => {
  if (!orgShortName.value) return preconfigureTouched.value ? "Required." : null;
  if (!/^[a-zA-Z0-9]+$/.test(orgShortName.value)) return "Letters and numbers only.";
  return null;
});

/**
 * Human-readable list of what's still blocking submission.
 *
 * Rendered next to the disabled Create button. Without this the form is a
 * dead end when the preconfigure sub-form is invalid: the button is disabled,
 * so a submit attempt can never happen, so the per-field "Required." errors
 * never appear and nothing tells you which field is at fault.
 */
const blockingReasons = computed<string[]>(() => {
  const out: string[] = [];
  if (!SLUG_RE.test(customer.value)) out.push("a valid customer slug");
  if (!dnsZone.value) out.push("a DNS zone");
  if (!sharedoVersion.value) out.push("an Operate version");
  if (preconfigureEnabled.value) {
    if (!orgName.value.trim()) out.push("organisation name");
    if (!orgShortName.value) out.push("organisation short name");
    else if (!/^[a-zA-Z0-9]+$/.test(orgShortName.value))
      out.push("organisation short name (letters and numbers only)");
    if (!adminFirstname.value.trim()) out.push("admin first name");
    if (!adminSurname.value.trim()) out.push("admin surname");
    if (!adminUsername.value.trim()) out.push("admin username");
    if (passwordErrors.value.length > 0) {
      out.push(
        adminPassword.value
          ? `a stronger admin password — ${passwordErrors.value[0].replace(/^Needs /, "needs ").replace(/\.$/, "")}`
          : "admin password",
      );
    }
  }
  return out;
});

const preconfigureComplete = computed(
  () =>
    !!orgName.value.trim() &&
    !!orgShortName.value &&
    /^[a-zA-Z0-9]+$/.test(orgShortName.value) &&
    !!adminFirstname.value.trim() &&
    !!adminSurname.value.trim() &&
    !!adminUsername.value.trim() &&
    passwordErrors.value.length === 0,
);

// Regex from ui-plan §5 / ui-ux-design.md §3.3.
const SLUG_RE = /^[a-z][a-z0-9-]{1,18}[a-z0-9]$/;

const slugError = computed(() => {
  if (!customerTouched.value) return null;
  if (!customer.value) return "Required.";
  if (!SLUG_RE.test(customer.value))
    return "Lowercase letters, digits, hyphens. 3–20 chars. Must start with a letter, end alphanumeric.";
  return null;
});

const fqdnPreview = computed(() =>
  customer.value && !slugError.value
    ? `${customer.value}.${dnsZone.value}`
    : null,
);

const canSubmit = computed(
  () =>
    !submitting.value &&
    SLUG_RE.test(customer.value) &&
    !!dnsZone.value &&
    !!sharedoVersion.value &&
    // A "custom" EE version must actually be typed in.
    (eeVersion.value !== "custom" || !!eeVersionCustom.value.trim()) &&
    (!preconfigureEnabled.value || preconfigureComplete.value),
);

const ring = computed(() => (channel.value === "release" ? "staging" : "playground"));

onMounted(async () => {
  try {
    const zones = await api.listDnsZones();
    dnsZones.value = zones;
    const def = zones.find((z) => z.default) ?? zones[0];
    if (def) dnsZone.value = def.zone;
  } catch {
    // Falls back to the hardcoded default — UI still works.
  }
  await loadVersions();
});

// Reload version list whenever the channel changes.
watch(channel, () => {
  void loadVersions();
});

async function loadVersions() {
  versionsLoading.value = true;
  versionsWarning.value = null;
  sharedoVersion.value = "";
  try {
    const r = await api.listSharedoVersions(ring.value as "staging" | "playground");
    versions.value = r.versions;
    versionsWarning.value = r.warning ?? null;
    if (r.versions.length > 0) {
      // Default to the newest (first in the list — backend orders desc).
      sharedoVersion.value = r.versions[0].version;
    }
  } catch (e) {
    versions.value = [];
    versionsWarning.value =
      e instanceof Error ? e.message : "Couldn't load versions.";
  } finally {
    versionsLoading.value = false;
  }
}

function formatPublishedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // YYYY-MM-DD; enough to spot which is newest at a glance without locale noise.
  return d.toISOString().substring(0, 10);
}

async function submit() {
  customerTouched.value = true;
  if (preconfigureEnabled.value) preconfigureTouched.value = true;
  if (!canSubmit.value) return;
  submitting.value = true;
  submitError.value = null;
  try {
    const res = await api.create({
      customer: customer.value,
      dnsZone: dnsZone.value,
      sharedoVersion: sharedoVersion.value || undefined,
      eeVersion: effectiveEeVersion.value || undefined,
      ring: ring.value,
      // Preconfigure supersedes the admin seed — the wizard creates the admin.
      seedAdminUser: preconfigureEnabled.value ? false : seedAdminUser.value,
      sidecarBackend: sidecarBackend.value,
      // platformUpgrade implies searchV2; force it on in the payload to match the server.
      searchV2: platformUpgrade.value || searchV2.value,
      platformUpgrade: platformUpgrade.value,
      preconfigure: preconfigureEnabled.value
        ? {
            organisation: {
              name: orgName.value.trim(),
              shortName: orgShortName.value.trim(),
            },
            localisation: {
              countrySystemName: countrySystemName.value,
              currencyIsoCode: currencyIsoCode.value,
              languageIsoCode: languageIsoCode.value,
            },
            adminUser: {
              firstname: adminFirstname.value.trim(),
              surname: adminSurname.value.trim(),
              username: adminUsername.value.trim(),
              password: adminPassword.value,
            },
            packages: selectedPackages.value,
          }
        : undefined,
    });
    void ensurePermission();
    router.replace(`/playgrounds/${res.customer}`);
  } catch (e) {
    if (e instanceof ConflictError) {
      submitError.value = `An \`${customer.value}\` playground already exists or is being deployed.`;
    } else {
      submitError.value = e instanceof Error ? e.message : "Failed to create playground.";
      pushToast(submitError.value, "error");
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <section class="mx-auto max-w-xl px-6 py-6">
    <nav class="mb-4 text-2xs">
      <router-link to="/" class="text-fg-muted hover:underline">&lt; Playgrounds</router-link>
    </nav>

    <h1 class="mb-5 text-base font-medium">New playground</h1>

    <form class="space-y-4" @submit.prevent="submit">
      <FormField label="Customer slug" :error="slugError">
        <template #default="{ id, describedById }">
          <input
            :id="id"
            v-model="customer"
            type="text"
            autocomplete="off"
            class="mono h-8 w-full rounded border bg-bg px-2 text-sm dark:bg-surface-dark"
            :class="slugError ? 'border-status-failed' : 'border-border dark:border-border-dark'"
            :aria-describedby="describedById"
            @blur="customerTouched = true"
            @input="customerTouched = true"
          />
        </template>
        <template #helper>
          Lowercase, digits, hyphens. 3–20 chars.<br />
          <span v-if="fqdnPreview">Will become: <span class="mono">{{ fqdnPreview }}</span></span>
          <span v-else>Will become: <span class="mono">&lt;slug&gt;.{{ dnsZone }}</span></span>
        </template>
      </FormField>

      <FormField label="DNS zone">
        <template #default="{ id }">
          <select
            :id="id"
            v-model="dnsZone"
            class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
          >
            <option
              v-for="z in dnsZones.length ? dnsZones : [{ zone: 'test.sharedo.cloud' }]"
              :key="z.zone"
              :value="z.zone"
            >
              {{ z.zone }}
            </option>
          </select>
        </template>
      </FormField>

      <FormField label="Operate version">
        <template #default>
          <div class="space-y-2">
            <!-- Channel selector: Release vs Playground. -->
            <div
              class="inline-flex overflow-hidden rounded border border-border dark:border-border-dark"
              role="radiogroup"
              aria-label="Deployment channel"
            >
              <button
                type="button"
                role="radio"
                :aria-checked="channel === 'release'"
                class="px-3 py-1.5 text-sm"
                :class="
                  channel === 'release'
                    ? 'bg-accent text-white dark:bg-accent-dark'
                    : 'bg-bg hover:bg-surface dark:bg-surface-dark dark:hover:bg-surface'
                "
                @click="channel = 'release'"
              >
                Release
              </button>
              <button
                type="button"
                role="radio"
                :aria-checked="channel === 'playground'"
                class="border-l border-border px-3 py-1.5 text-sm dark:border-border-dark"
                :class="
                  channel === 'playground'
                    ? 'bg-accent text-white dark:bg-accent-dark'
                    : 'bg-bg hover:bg-surface dark:bg-surface-dark dark:hover:bg-surface'
                "
                @click="channel = 'playground'"
              >
                Playground
              </button>
            </div>

            <!-- Version dropdown for the selected channel. -->
            <select
              v-model="sharedoVersion"
              :disabled="versionsLoading || versions.length === 0"
              class="h-8 w-full rounded border border-border bg-bg px-2 text-sm disabled:opacity-50 dark:border-border-dark dark:bg-surface-dark"
            >
              <option v-if="versionsLoading" value="">Loading…</option>
              <option v-else-if="versions.length === 0" value="">No versions available</option>
              <option v-for="v in versions" :key="v.version" :value="v.version">
                {{ v.version }}
                <span v-if="v.publishedAt"> · {{ formatPublishedAt(v.publishedAt) }}</span>
              </option>
            </select>

            <p class="text-2xs text-fg-muted">
              <span v-if="channel === 'release'">
                Last 5 official versions from the staging release pipeline, newest first.
              </span>
              <span v-else>
                Experimental builds — hackathons, MVPs, work awaiting review.
              </span>
            </p>

            <p v-if="versionsWarning" class="text-2xs text-status-failed">
              {{ versionsWarning }}
            </p>
          </div>
        </template>
      </FormField>

      <FormField label="Event Engine version">
        <template #default="{ id }">
          <select
            :id="id"
            v-model="eeVersion"
            class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
          >
            <option value="pinned">pinned (recommended)</option>
            <option value="latest">latest</option>
            <option value="custom">custom version…</option>
          </select>
          <input
            v-if="eeVersion === 'custom'"
            v-model="eeVersionCustom"
            type="text"
            placeholder="e.g. 0.0.0.1"
            class="mt-2 h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
          />
        </template>
        <template #helper>
          <span v-if="eeVersion === 'custom'">
            Exact EE build to pull (e.g. <span class="mono">0.0.0.1</span>, the platform-upgrade
            EE with the Kurrent client) — bare semver, no <span class="mono">v</span> prefix. Must
            already be published to the EE blob store.
          </span>
          <span v-else>
            'pinned' uses the EE version matching the selected Operate release.
          </span>
        </template>
      </FormField>

      <FormField label="Admin user">
        <template #default="{ id }">
          <label
            class="inline-flex items-center gap-2 text-sm"
            :class="preconfigureEnabled ? 'opacity-50' : ''"
          >
            <input
              :id="id"
              v-model="seedAdminUser"
              type="checkbox"
              :disabled="preconfigureEnabled"
            />
            Seed admin user (older builds without StartupWizard)
          </label>
        </template>
        <template #helper>
          <span v-if="preconfigureEnabled">
            Not needed — you're preconfiguring the environment below, and the Startup Wizard's own
            admin-user step creates the administrator.
          </span>
          <span v-else>
            Older Sharedo playground builds don't have the StartupWizard. Enabling this provisions a single
            'admin' user with a randomly-generated password (exposed in the dashboard) and removes the
            seeded demo users. Skip for blank-sharedo-derivative builds.
          </span>
        </template>
      </FormField>

      <!-- Preconfigure the environment (task BP). Toggle + revealed sub-form,
           mirroring the upgrade dialog's pattern in PlaygroundDetail.vue. -->
      <FormField label="Preconfigure environment">
        <template #default="{ id }">
          <label class="inline-flex items-center gap-2 text-sm">
            <input :id="id" v-model="preconfigureEnabled" type="checkbox" />
            Preconfigure the environment (answer the Startup Wizard up front)
          </label>
        </template>
        <template #helper>
          Newer builds boot into a Startup Wizard that someone has to click through before the
          environment is usable. Fill these in and the deploy answers it for you, then completes it —
          the trial arrives ready to sign into.
        </template>
      </FormField>

      <div
        v-if="preconfigureEnabled"
        class="space-y-4 rounded border border-border bg-surface p-4 dark:border-border-dark dark:bg-surface-dark"
      >
        <p class="text-2xs text-fg-muted">
          Applied as the final deploy step. If it can't be applied the deploy still succeeds and the
          environment simply arrives on the wizard, ready to complete by hand.
        </p>

        <FormField label="Organisation name" :error="preconfigureTouched && !orgName.trim() ? 'Required.' : undefined">
          <template #default="{ id }">
            <input
              :id="id"
              v-model="orgName"
              type="text"
              placeholder="Example Legal LLP"
              class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
            />
          </template>
        </FormField>

        <FormField label="Organisation short name" :error="shortNameError ?? undefined">
          <template #default="{ id }">
            <input
              :id="id"
              v-model="orgShortName"
              type="text"
              placeholder="examplelegal"
              class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
              @input="orgShortNameEdited = true"
            />
          </template>
          <template #helper>
            Letters and numbers only. Derived from the organisation name until you edit it.
          </template>
        </FormField>

        <div class="grid grid-cols-3 gap-2">
          <FormField label="Country">
            <template #default="{ id }">
              <select
                :id="id"
                v-model="countrySystemName"
                class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
              >
                <option v-for="c in COUNTRIES" :key="c.value" :value="c.value">{{ c.label }}</option>
              </select>
            </template>
          </FormField>
          <FormField label="Currency">
            <template #default="{ id }">
              <select
                :id="id"
                v-model="currencyIsoCode"
                class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
              >
                <option v-for="c in CURRENCIES" :key="c.value" :value="c.value">{{ c.label }}</option>
              </select>
            </template>
          </FormField>
          <FormField label="Language">
            <template #default="{ id }">
              <select
                :id="id"
                v-model="languageIsoCode"
                class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
              >
                <option v-for="l in LANGUAGES" :key="l.value" :value="l.value">{{ l.label }}</option>
              </select>
            </template>
          </FormField>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <FormField
            label="Admin first name"
            :error="preconfigureTouched && !adminFirstname.trim() ? 'Required.' : undefined"
          >
            <template #default="{ id }">
              <input
                :id="id"
                v-model="adminFirstname"
                type="text"
                placeholder="Alex"
                class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
              />
            </template>
          </FormField>
          <FormField
            label="Admin surname"
            :error="preconfigureTouched && !adminSurname.trim() ? 'Required.' : undefined"
          >
            <template #default="{ id }">
              <input
                :id="id"
                v-model="adminSurname"
                type="text"
                placeholder="Davenport"
                class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
              />
            </template>
          </FormField>
        </div>

        <FormField
          label="Admin username"
          :error="preconfigureTouched && !adminUsername.trim() ? 'Required.' : undefined"
        >
          <template #default="{ id }">
            <input
              :id="id"
              v-model="adminUsername"
              type="text"
              placeholder="alex.davenport@firm.com"
              class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
            />
          </template>
        </FormField>

        <FormField
          label="Admin password"
          :error="(adminPassword || preconfigureTouched) && passwordErrors.length > 0 ? passwordErrors[0] : undefined"
        >
          <template #default="{ id }">
            <input
              :id="id"
              v-model="adminPassword"
              type="password"
              class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
            />
          </template>
          <template #helper>
            8+ characters with upper and lower case, a number and a special character. Stored in Key
            Vault and revealed by the playground's admin login panel.
          </template>
        </FormField>

        <FormField label="Configuration packs">
          <template #default>
            <div class="space-y-2">
              <label
                v-for="p in PACKAGES"
                :key="p.value"
                class="flex items-start gap-2 text-sm"
              >
                <input
                  v-model="selectedPackages"
                  type="checkbox"
                  :value="p.value"
                  class="mt-1"
                />
                <span>
                  {{ p.label }}
                  <span class="block text-2xs text-fg-muted">{{ p.help }}</span>
                </span>
              </label>
            </div>
          </template>
          <template #helper>
            Imported during the wizard. A pack import adds a few minutes to the deploy.
          </template>
        </FormField>
      </div>

      <FormField label="Sidecar backend">
        <template #default="{ id }">
          <select
            :id="id"
            v-model="sidecarBackend"
            class="h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
          >
            <option value="aci">ACI (default)</option>
            <option value="aks">AKS (shared cluster)</option>
          </select>
        </template>
        <template #helper>
          Where the per-customer Elasticsearch + EventStore run. <strong>ACI</strong> (default) is the
          established path. <strong>AKS</strong> places them on the shared AKS cluster — durable
          managed-disk storage (no ephemeral wipe) and a stable pinned IP (no drift). Opt-in; requires
          the shared AKS cluster to be provisioned.
        </template>
      </FormField>

      <FormField label="Platform upgrade">
        <template #default="{ id }">
          <label class="inline-flex items-center gap-2 text-sm">
            <input
              :id="id"
              v-model="platformUpgrade"
              type="checkbox"
            />
            Full platform upgrade (KurrentDB 26.0 + OpenIddict + Elasticsearch 9 / net10)
          </label>
        </template>
        <template #helper>
          Off (default) deploys the established platform (EventStore 4.1.2). On deploys the fully
          upgraded platform for the <strong>sr-operate-platform-upgrade</strong> build:
          <strong>KurrentDB 26.0</strong> (gRPC over TLS) instead of EventStore 4.1.2, OpenIddict
          identity, and the v2 search/indexer tier below. Forces the v2 tier on. Requires the selected
          release to be a platform-upgrade build.
        </template>
      </FormField>

      <FormField label="Search / indexer tier">
        <template #default="{ id }">
          <label class="inline-flex items-center gap-2 text-sm">
            <input
              :id="id"
              :checked="searchV2 || platformUpgrade"
              :disabled="platformUpgrade"
              type="checkbox"
              @change="searchV2 = ($event.target as HTMLInputElement).checked"
            />
            .NET 10 v2 tier (Elasticsearch 9 + indexer-v2 / search-v2)
          </label>
        </template>
        <template #helper>
          Off (default) deploys the established <strong>Elasticsearch 6</strong> + .NET 4.8 indexer with
          in-process search. On deploys the clean-swap <strong>v2 tier</strong>: secured Elasticsearch 9
          plus the net10 indexer-v2 / search-v2 container apps (the 4.8 indexer + ES 6 are dropped, EE
          indexer roles disabled). Requires the selected release to carry the net10 package.
          <em>Forced on when Platform upgrade is selected.</em>
        </template>
      </FormField>

      <p
        class="rounded border border-border bg-surface p-3 text-2xs text-fg-muted dark:border-border-dark dark:bg-surface-dark"
      >
        Heads up: deploys take 10–15 minutes. You can close this tab; the deploy keeps running.
      </p>

      <p v-if="submitError" class="text-2xs text-status-failed">{{ submitError }}</p>

      <div class="flex justify-end gap-2 pt-2">
        <router-link
          to="/"
          class="rounded border border-border px-3 py-1.5 text-sm hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
        >Cancel</router-link>
        <button
          type="submit"
          class="inline-flex items-center gap-2 rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-accent-dark"
          :disabled="!canSubmit"
        >
          <Spinner v-if="submitting" :size="14" /> Create playground
        </button>
      </div>

      <p
        v-if="!submitting && blockingReasons.length > 0"
        class="text-right text-2xs text-fg-muted"
      >
        Still needed: {{ blockingReasons.join(", ") }}.
      </p>
    </form>
  </section>
</template>
